// v4.2.0 — ContentDetail (page dédiée au journal, universelle).
// Étape 4 de la maquette : header + onglets rubriques + grande couverture +
// boutons "Lire le journal" / prix. Aucune connaissance du type de média.

import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Share2, Bookmark, BookOpen } from "lucide-react";
import ReadAloudPrompt from "@/components/content/ReadAloudPrompt";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function ContentDetail({ content, owned, onBack, onRead, onPurchased }) {
  const [processing, setProcessing] = useState(false);
  const [justPurchased, setJustPurchased] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [bookmarked, setBookmarked] = useState(
    () => localStorage.getItem(`aya_bookmark_${content?.id}`) === "1",
  );

  const sections = useMemo(() => {
    const seen = [];
    for (const h of content?.page_headlines || []) {
      if (h.section && !seen.includes(h.section)) seen.push(h.section);
    }
    return seen;
  }, [content]);

  if (!content) return null;
  const currentSection = activeSection || sections[0];

  function startPageFor(section) {
    const hit = (content.page_headlines || []).find((h) => h.section === section);
    return hit ? hit.number : 1;
  }

  async function handleBuy() {
    const token = localStorage.getItem("civ_user_token");
    if (!token) return toast.error("Connexion requise");
    try {
      setProcessing(true);
      const r = await axios.post(
        `${BACKEND_URL}/api/v3/content/purchase`,
        { resource_id: content.id },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const verdict = r.data?.verdict;
      if (verdict === "APPROVED" || verdict === "ALREADY_OWNED") {
        toast.success(verdict === "APPROVED" ? "✅ Paiement réussi ! Votre journal est disponible." : "Déjà acheté");
        setJustPurchased(true);
        onPurchased && onPurchased(r.data);
      } else if (verdict === "INSUFFICIENT_FUNDS") {
        toast.error(`Solde insuffisant · ${r.data.balance} FCFA disponibles`);
      } else {
        toast.error(r.data?.error || "Achat impossible");
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erreur réseau");
    } finally {
      setProcessing(false);
    }
  }

  function toggleBookmark() {
    const next = !bookmarked;
    setBookmarked(next);
    localStorage.setItem(`aya_bookmark_${content.id}`, next ? "1" : "0");
    toast.success(next ? "Ajouté aux favoris" : "Retiré des favoris");
  }

  async function handleShare() {
    const text = `${content.title} — édition du ${content.edition_date}`;
    try {
      if (navigator.share) await navigator.share({ title: content.title, text });
      else {
        await navigator.clipboard.writeText(text);
        toast.success("Lien copié");
      }
    } catch (_) {}
  }

  return (
    <div className="min-h-screen bg-[#0a0a0d] text-white" data-testid={`content-detail-${content.id}`}>
      {/* Header */}
      <div className="sticky top-0 z-20 bg-[#0a0a0d]/95 backdrop-blur border-b border-white/8">
        <div className="max-w-3xl mx-auto flex items-center gap-3 px-4 py-3">
          <button onClick={onBack} data-testid="content-detail-back" className="p-1.5 -ml-1.5 rounded-full hover:bg-white/10 transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[15px] font-bold leading-tight truncate">{content.title}</div>
            <div className="text-[11px] text-white/50">{content.edition_date}</div>
          </div>
          <button onClick={handleShare} data-testid="content-detail-share" className="p-2 rounded-full hover:bg-white/10 transition-colors">
            <Share2 size={18} />
          </button>
          <button onClick={toggleBookmark} data-testid="content-detail-bookmark" className="p-2 rounded-full hover:bg-white/10 transition-colors">
            <Bookmark size={18} className={bookmarked ? "fill-amber-400 text-amber-400" : ""} />
          </button>
        </div>

        {/* Onglets rubriques */}
        {sections.length > 0 && (
          <div className="max-w-3xl mx-auto flex gap-2 px-4 pb-3 overflow-x-auto no-scrollbar" data-testid="content-detail-tabs">
            {sections.map((s) => (
              <button
                key={s}
                onClick={() => setActiveSection(s)}
                data-testid={`content-detail-tab-${s}`}
                className={`shrink-0 px-4 py-1.5 rounded-full text-[13px] font-medium transition-all ${
                  s === currentSection
                    ? "bg-amber-400 text-black"
                    : "text-white/60 hover:text-white bg-white/5"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Couverture ou confirmation d'achat */}
      <div className="max-w-3xl mx-auto px-4 pt-5 pb-32">
        {justPurchased ? (
          <div className="max-w-md mx-auto" data-testid="purchase-confirmation-panel">
            <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-emerald-300 text-sm font-semibold">
              ✅ Paiement réussi ! Votre {content.variant === "MAGAZINE" ? "magazine" : "journal"} est disponible.
            </div>
            <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <div className="flex items-center gap-4">
                {content.cover_url && (
                  <div className="w-16 h-[84px] rounded-md overflow-hidden bg-white shrink-0 shadow-lg">
                    <img src={content.cover_url} alt={content.title} className="w-full h-full object-cover object-top" />
                  </div>
                )}
                <div>
                  <div className="text-base font-bold" style={{ fontFamily: "Georgia, serif" }}>{content.title}</div>
                  <div className="mt-2 space-y-1.5 text-[13px] text-white/80">
                    <div className="flex items-center gap-2"><span className="text-emerald-400">✓</span> {content.variant === "MAGAZINE" ? "Magazine" : "Journal"} acheté</div>
                    <div className="flex items-center gap-2"><span className="text-emerald-400">✓</span> Accès illimité</div>
                    <div className="flex items-center gap-2"><span className="text-emerald-400">✓</span> Valide sur tous vos appareils</div>
                  </div>
                </div>
              </div>
              <button
                onClick={() => onRead(1)}
                data-testid="read-now-btn"
                className="mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-amber-400/60 bg-amber-400/10 text-amber-300 font-semibold text-[15px] hover:bg-amber-400/20 transition-all"
              >
                <BookOpen size={17} />
                Lire le journal maintenant
              </button>
            </div>
            <ReadAloudPrompt content={content} />
          </div>
        ) : (
          <>
            <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-white max-w-md mx-auto">
              {content.cover_url ? (
                <img
                  src={content.cover_url}
                  alt={content.title}
                  className="w-full object-cover"
                  data-testid="content-detail-cover"
                />
              ) : (
                <div className="aspect-[3/4] flex items-center justify-center text-6xl bg-neutral-100">📰</div>
              )}
            </div>

            {content.description && (
              <p className="text-sm text-white/60 leading-relaxed mt-5 max-w-md mx-auto text-center">
                {content.description}
              </p>
            )}
          </>
        )}
      </div>

      {/* Boutons bas fixes */}
      {!justPurchased && (
      <div className="fixed bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-[#0a0a0d] via-[#0a0a0d]/95 to-transparent pt-8 pb-[calc(env(safe-area-inset-bottom)+16px)]">
        <div className="max-w-3xl mx-auto flex gap-3 px-4">
          <button
            onClick={() => (owned ? onRead(startPageFor(currentSection)) : handleBuy())}
            disabled={processing}
            data-testid="content-detail-read-btn"
            className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl border border-amber-400/60 bg-amber-400/10 text-amber-300 font-semibold text-[15px] hover:bg-amber-400/20 transition-all disabled:opacity-50"
          >
            <BookOpen size={18} />
            Lire le journal
          </button>
          <button
            onClick={owned ? () => onRead(startPageFor(currentSection)) : handleBuy}
            disabled={processing || owned}
            data-testid="content-detail-price-btn"
            className={`px-6 py-3.5 rounded-2xl font-bold text-[15px] transition-all disabled:opacity-90 ${
              owned
                ? "border border-emerald-400/50 bg-emerald-400/10 text-emerald-300"
                : "border border-amber-400/60 bg-amber-400/10 text-amber-300 hover:bg-amber-400/20"
            }`}
          >
            {processing ? "…" : owned ? "✓ Acheté" : `${content.price_fcfa} FCFA`}
          </button>
        </div>
      </div>
      )}
    </div>
  );
}
